
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { brandTeamService } from './brandTeamService';
import { BrandRole, BrandTeamMember, SystemRole } from './types';
import { teamMemberService } from './services/teamMemberService';

export { type BrandRole, type BrandTeamMember, type SystemRole } from './types';

export const useBrandTeam = (brandId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState<BrandTeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState<BrandRole | null>(null);

  const fetchTeamMembers = async () => {
    if (!user || !brandId) {
      setTeamMembers([]);
      setUserRole(null);
      return;
    }

    try {
      setIsLoading(true);
      
      // Check user access
      const userAccess = await brandTeamService.checkUserAccess(brandId, user.id);
      
      if (!userAccess) {
        setTeamMembers([]);
        setUserRole(null);
        return;
      }

      setUserRole(userAccess.role as BrandRole);

      // Get team members
      const members = await brandTeamService.getTeamMembers(brandId);
      setTeamMembers(members);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
      toast({
        title: "Error",
        description: "Failed to fetch team members",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inviteMember = async (email: string, role: BrandRole, customRoleId?: string, createSquadMember?: boolean, squadType?: string): Promise<boolean> => {
    if (!user || !brandId) return false;

    try {
      setIsLoading(true);
      await brandTeamService.inviteMember(email, role, brandId, user.id, customRoleId, createSquadMember, squadType);
      
      toast({
        title: "Success",
        description: "Team member invited successfully",
      });

      await fetchTeamMembers();
      return true;
    } catch (error: any) {
      console.error('Error inviting member:', error);
      
      // Use the exact error message from the service
      const errorMessage = error.message || "Failed to invite team member";
      
      console.log('Showing toast with error:', errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateMemberRole = async (memberId: string, newRole: BrandRole, customRoleId?: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      await brandTeamService.updateMemberRole(memberId, newRole, customRoleId);

      toast({
        title: "Success",
        description: "Member role updated successfully",
      });

      await fetchTeamMembers();
      return true;
    } catch (error: any) {
      console.error('Error updating member role:', error);
      toast({
        title: "Error",
        description: "Failed to update member role",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const removeMember = async (memberId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      await brandTeamService.removeMember(memberId);

      toast({
        title: "Success",
        description: "Team member removed successfully",
      });

      await fetchTeamMembers();
      return true;
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: "Failed to remove team member",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateSystemRole = async (userId: string, newRole: SystemRole): Promise<boolean> => {
    if (!user) return false;

    try {
      await teamMemberService.updateSystemRole(userId, newRole);

      toast({
        title: "Success",
        description: "System role updated successfully",
      });

      await fetchTeamMembers();
      return true;
    } catch (error: any) {
      console.error('Error updating system role:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update system role",
        variant: "destructive",
      });
      return false;
    }
  };

  const canManageTeam = () => {
    return userRole === 'owner' || userRole === 'admin';
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [user, brandId]);

  return {
    teamMembers,
    isLoading,
    userRole,
    canManageTeam: canManageTeam(),
    inviteMember,
    updateMemberRole,
    removeMember,
    updateSystemRole,
    refetch: fetchTeamMembers,
  };
};
